import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Eye, Loader2, Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useResourceSets } from 'components/IAP/hooks/resourceSetHooks';
import { useStrikeTeams } from 'components/IAP/hooks/strikeTeamHooks';
import apiConfig from 'constants/apiConfig';
import {
  isTropicalSourcePreset,
  useCreateOrgEventAutomationRule,
  useDeleteOrgEventAutomationRule,
  useOrgEventAutomationRules,
  usePreviewOrgEventAutomationRule,
  useUpdateOrgEventAutomationRule,
  type OrgEventAutomationRule,
  type OrgEventChecklistItemDef,
  type PreviewAutomationRuleResult,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { useAors } from 'hooks/useAors';
import { useAorText } from 'utils/aorTerminology';
import { useRosters } from 'hooks/useRosters';
import {
  selectGroupGuid,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import {
  roleScopeOptions,
  type RoleScopeMember,
} from '../NotificationsTab/audienceModel';
import { ReachabilityWarning } from '../NotificationsTab/ReachabilityWarning';
import {
  AUTOMATION_CHANNEL_OPTIONS,
  AUTOMATION_PRIORITY_OPTIONS,
  DEFAULT_UPDATE_MIN_INTERVAL_MINUTES,
  effectiveUpdateNotificationTypes,
  LINKED_SMS_MAX_LENGTH,
  SMS_PREVIEW_BODY_TEMPLATE,
  smsPreviewWithLink,
  UPDATE_INTERVAL_OPTIONS,
} from './automationNotifyForm';
import { NotificationType } from 'hooks/useNotification';
import {
  DESCRIPTION_PLACEHOLDERS,
  EVENT_NAME_PLACEHOLDERS,
  eventNamePreviewVars,
  insertPlaceholder,
  renderTemplatePreview,
  sampleTemplateVars,
} from './eventNameTemplatePreview';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import { resolvedCategory } from './eventCategory';
import { CategoryFields, SeverityToggle } from './eventCategoryFields';
import { RulePreviewDialog } from './RulePreviewDialog';
import {
  saveEnableConfirmIntervalText,
  triggerSourceLabels,
} from './saveEnableConfirm';
import { SaveAutomationRuleDialog } from './SaveAutomationRuleDialog';
import {
  describeRule,
  formatAutomationRuleFormula,
  normalizeAutomationRuleLogic,
  type AutomationRuleLogicSlice,
} from './automationRuleLogic';
import {
  blankRuleForm,
  checkFrequencyLabel,
  checkFrequencyOptionsFor,
  clampCheckFrequencyFor,
  conditionsReadFeed,
  formToPayload,
  logicSliceFromForm,
  payloadToForm,
  primarySourceFromConditions,
  ruleToForm,
  type RuleDraftPayload,
  type RuleFormValues,
} from './ruleFormModel';
import {
  CheckboxRow,
  HelpIcon,
  SimpleSelect,
  TemplateVariableChips,
  ToggleRow,
} from './automationFormControls';
import { registerEventsTourApi } from '../../eventsTour/eventsTourBridge';
import { FEED_FREQUENCY_NOTE } from './externalFeeds';
import type { EventsTourStepId } from '../../eventsTour/eventsTourSteps';
import { CategoryChecklistAssets } from './CategoryChecklistAssetsPanel';
import { filterResourcesAssignedToAutomationRule } from './categoryChecklistMatch';
import { ChecklistConfigEditor } from './ChecklistConfigEditor';
import { ConditionBuilder } from './ConditionBuilder';
import { RuleStepNav } from './RuleStep';
import {
  assetChecklistsStep,
  channelLabels,
  checklistStep,
  conditionErrors,
  conditionsStep,
  eventStep,
  notifyRecipientCount,
  notifyStep,
  ruleErrors,
  type RuleStepId,
} from './ruleStepModel';

const RULE_STEP_NAV: {
  id: RuleStepId;
  index: number;
  title: string;
}[] = [
  { id: 'conditions', index: 1, title: 'When' },
  { id: 'event', index: 2, title: 'Then create an event' },
  { id: 'notify', index: 3, title: 'Notify people' },
  { id: 'checklist', index: 4, title: 'Response Plan' },
  { id: 'assetChecklists', index: 5, title: 'Per-asset response plans' },
];

/**
 * Automation rule editor. The step list stays on the left while only the
 * selected step's form scrolls.
 *
 * Shared internals (condition editing, form model, controls) live in sibling
 * modules.
 */
export function RuleEditorSteps({
  rule,
  onClose,
  initialPayload,
  onSubmitPayload,
  externalPayload,
  payloadRef,
  hideCancel = false,
}: {
  /** Null opens an unsaved draft. Save creates the rule. */
  rule: OrgEventAutomationRule | null;
  onClose: () => void;
  /** Pre-fills an unsaved draft (read once, when the editor mounts). */
  initialPayload?: RuleDraftPayload;
  /**
   * Replaces the create/update call after the enable prompt. The caller owns
   * persistence, and `onClose` is not called.
   */
  onSubmitPayload?: (payload: RuleDraftPayload) => void | Promise<void>;
  /** Replaces the form whenever `nonce` changes (e.g. a chat revision). */
  externalPayload?: { payload: RuleDraftPayload; nonce: number };
  /** Set to a getter for the current form as a payload (unsaved). */
  payloadRef?: MutableRefObject<(() => RuleDraftPayload) | null>;
  /** The host renders its own close/discard control. */
  hideCancel?: boolean;
}) {
  const groupGuid = useAppSelector(selectGroupGuid);
  const userGuid = useAppSelector(selectUserGuid);
  const { data: rosters } = useRosters(groupGuid ? [groupGuid] : []);
  const { data: aors = [] } = useAors();
  const aorText = useAorText();
  const { data: strikeTeams = [] } = useStrikeTeams();
  const { data: resources = [] } = useResourceSets();
  const { data: automationRules = [] } = useOrgEventAutomationRules();
  const createRule = useCreateOrgEventAutomationRule();
  const updateRule = useUpdateOrgEventAutomationRule();
  const deleteRule = useDeleteOrgEventAutomationRule();
  const previewRule = usePreviewOrgEventAutomationRule();
  const { data: roleScopeMembers = [] } = useQuery({
    queryKey: ['role-scope-members', groupGuid],
    enabled: Boolean(groupGuid),
    queryFn: async () => {
      const response = await axios.post<RoleScopeMember[]>(
        apiConfig.apiGateway.roleScopeMembersForGroup,
        { group_guid: groupGuid }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
  });

  const [openStep, setOpenStep] = useState<RuleStepId>('conditions');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewResult, setPreviewResult] =
    useState<PreviewAutomationRuleResult | null>(null);
  const thresholdTemplateRef = useRef<HTMLTextAreaElement | null>(null);
  const eventNameTemplateRef = useRef<HTMLInputElement | null>(null);

  const initialPayloadRef = useRef(initialPayload);
  const startingValues = (): RuleFormValues => {
    if (rule) return ruleToForm(rule);
    if (initialPayloadRef.current) {
      return payloadToForm(initialPayloadRef.current);
    }
    return blankRuleForm(userGuid);
  };

  const form = useForm<RuleFormValues>({
    defaultValues: startingValues(),
  });

  useEffect(() => {
    form.reset(startingValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule, form, userGuid]);

  const externalNonce = externalPayload?.nonce;
  useEffect(() => {
    if (!externalPayload) return;
    form.reset(payloadToForm(externalPayload.payload));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalNonce, form]);

  const watched = useWatch({ control: form.control });
  const conditions = form.watch('conditions') ?? [];
  const logicBlocks = form.watch('logicBlocks') ?? [];
  const enabled = !!form.watch('enabled');
  const checklistItems = form.watch('checklistItems') ?? [];
  const aorStrategy = form.watch('aorStrategy');

  /** Reactive full-form snapshot the pure step model reads. */
  const values = useMemo(
    () =>
      ({
        ...(watched as RuleFormValues),
        conditions,
        logicBlocks,
      }) as RuleFormValues,
    [watched, conditions, logicBlocks]
  );

  const aorNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const aor of aors) map.set(String(aor.id), aor.name);
    return map;
  }, [aors]);

  useEffect(() => {
    if (!payloadRef) return undefined;
    payloadRef.current = () => formToPayload(form.getValues(), aorNameById);
    return () => {
      payloadRef.current = null;
    };
  }, [payloadRef, form, aorNameById]);

  const aorOptions = useMemo(
    () => aors.map((aor) => ({ value: String(aor.id), label: aor.name })),
    [aors]
  );

  const userOptions = useMemo(
    () =>
      (rosters ?? []).map((u) => ({
        value: u.user_guid,
        label: u.email_address,
      })),
    [rosters]
  );

  const teamOptions = useMemo(
    () =>
      strikeTeams.map((team) => ({
        value: team.strike_team_id || team.id,
        label: team.name,
      })),
    [strikeTeams]
  );

  const savedRuleName = rule?.name ?? '';
  const assignmentName =
    savedRuleName.trim() || String(values.name ?? '').trim();
  const assignedAssetCount = useMemo(
    () =>
      assignmentName
        ? filterResourcesAssignedToAutomationRule(
            resources,
            assignmentName,
            automationRules
          ).length
        : 0,
    [resources, assignmentName, automationRules]
  );

  const applyLogicSlice = (next: AutomationRuleLogicSlice) => {
    form.setValue('conditions', next.conditions, { shouldDirty: true });
    form.setValue('logicBlocks', next.logicBlocks, { shouldDirty: true });
    form.setValue('logic', next.logic === 'any' ? 'any' : 'all', {
      shouldDirty: true,
    });
    form.setValue('source', primarySourceFromConditions(next.conditions), {
      shouldDirty: true,
    });
    const currentPattern = form.getValues('repeatPattern');
    const allowedPattern = clampCheckFrequencyFor(
      currentPattern,
      next.conditions
    );
    if (allowedPattern !== currentPattern) {
      form.setValue('repeatPattern', allowedPattern, { shouldDirty: true });
    }
  };

  const readsFeed = conditionsReadFeed(values.conditions, values.source);
  const frequencyOptions = checkFrequencyOptionsFor(
    values.conditions,
    values.source
  );

  const currentLogicSlice = useMemo(
    () => normalizeAutomationRuleLogic(logicSliceFromForm(values)),
    [values]
  );

  const usesTropicalFeed =
    isTropicalSourcePreset(form.getValues('source')) ||
    conditions.some((c) => isTropicalSourcePreset(c.source));

  const previewVars = useMemo(
    () =>
      sampleTemplateVars({
        tropical: usesTropicalFeed,
        layerLabel: primarySourceFromConditions(conditions)?.layerLabel,
      }),
    [usesTropicalFeed, conditions]
  );

  const hasAorIntersect = conditions.some((c) => c.kind === 'aor_intersect');
  const eventNamePlaceholders = EVENT_NAME_PLACEHOLDERS.filter(
    (placeholder) =>
      (!placeholder.tropicalOnly || usesTropicalFeed) &&
      (!placeholder.aorOnly || hasAorIntersect)
  );
  const descriptionPlaceholders = DESCRIPTION_PLACEHOLDERS.filter(
    (placeholder) => !placeholder.aorOnly || hasAorIntersect
  );

  const eventNamePreview = renderTemplatePreview(
    watched.eventName as string | undefined,
    eventNamePreviewVars({
      ruleName: watched.name as string | undefined,
      layerLabel: primarySourceFromConditions(conditions)?.layerLabel,
    })
  );
  const thresholdTemplatePreview = renderTemplatePreview(
    watched.thresholdDescription as string | undefined,
    previewVars
  );

  const formulaPreview = useMemo(
    () => formatAutomationRuleFormula(currentLogicSlice),
    [currentLogicSlice]
  );

  const notifyChannels = channelLabels(values.notificationTypes ?? []);
  const notifyCount = notifyRecipientCount(values);
  const updateChannels = effectiveUpdateNotificationTypes({
    notificationTypes: values.notificationTypes ?? [],
    updateNotificationTypes: values.updateNotificationTypes ?? null,
  });
  const sendsSms =
    (values.notificationTypes ?? []).includes(NotificationType.SMS) ||
    (values.notifyOnDataChange !== false &&
      updateChannels.includes(NotificationType.SMS));
  const smsPreview = sendsSms
    ? smsPreviewWithLink(
        renderTemplatePreview(SMS_PREVIEW_BODY_TEMPLATE, {
          ...previewVars,
          eventName: eventNamePreview || 'Event',
        })
      )
    : '';

  const readsAs = useMemo(
    () =>
      describeRule(currentLogicSlice, {
        eventName: eventNamePreview,
        notifyChannels,
        notifyRecipientCount: notifyCount,
        checklistItemCount: checklistItems.length,
      }),
    [
      currentLogicSlice,
      eventNamePreview,
      notifyChannels,
      notifyCount,
      checklistItems.length,
    ]
  );

  const steps = {
    conditions: conditionsStep(values),
    event: eventStep(values),
    notify: notifyStep(values),
    checklist: checklistStep(values),
    assetChecklists: assetChecklistsStep(assignedAssetCount),
  };
  const blockingErrors = ruleErrors(values);

  const setChecklistItems = (next: OrgEventChecklistItemDef[]) => {
    form.setValue('checklistItems', next, { shouldDirty: true });
  };

  const insertTemplatePlaceholder = (
    fieldName: 'thresholdDescription' | 'eventName',
    element: HTMLInputElement | HTMLTextAreaElement | null,
    token: string
  ) => {
    const current = (form.getValues(fieldName) as string) ?? '';
    const { value, caret } = insertPlaceholder(
      current,
      token,
      element?.selectionStart,
      element?.selectionEnd
    );
    form.setValue(fieldName, value, { shouldDirty: true });
    // Restore the caret after React re-renders with the new value.
    requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(caret, caret);
    });
  };

  const [submittingPayload, setSubmittingPayload] = useState(false);
  const saving =
    updateRule.isPending || createRule.isPending || submittingPayload;

  const requestSave = () => {
    const submitted = form.getValues();
    if (ruleErrors(submitted).length > 0) {
      if (conditionErrors(submitted).length > 0) setOpenStep('conditions');
      return;
    }
    setSaveConfirmOpen(true);
  };

  const persistSave = async (enabledOnSave: boolean) => {
    const submitted = form.getValues();
    if (ruleErrors(submitted).length > 0) {
      if (conditionErrors(submitted).length > 0) setOpenStep('conditions');
      setSaveConfirmOpen(false);
      return;
    }
    const payload = formToPayload(
      { ...submitted, enabled: enabledOnSave },
      aorNameById
    );
    if (onSubmitPayload) {
      setSubmittingPayload(true);
      try {
        await onSubmitPayload(payload);
      } finally {
        setSubmittingPayload(false);
        setSaveConfirmOpen(false);
      }
      return;
    }
    if (!rule) {
      if (!groupGuid) return;
      await createRule.mutateAsync({
        ...payload,
        group_guid: groupGuid,
        created_by_user_guid: userGuid ?? null,
      });
    } else {
      await updateRule.mutateAsync({
        id: rule.id,
        ...payload,
      });
    }
    setSaveConfirmOpen(false);
    onClose();
  };

  const saveIntervalText = saveEnableConfirmIntervalText(
    values.repeatPattern,
    triggerSourceLabels(values)
  );

  const selectStep = (id: RuleStepId) => setOpenStep(id);

  useEffect(() => {
    return registerEventsTourApi({
      prepareRuleStep: (id: EventsTourStepId) => {
        if (id === 'when' || id === 'checkFrequency') {
          setOpenStep('conditions');
          return;
        }
        if (id === 'event') {
          setOpenStep('event');
          return;
        }
        if (id === 'notify') {
          setOpenStep('notify');
          return;
        }
        if (id === 'checklist') {
          setOpenStep('checklist');
          return;
        }
        if (id === 'assetChecklists') {
          setOpenStep('assetChecklists');
        }
      },
    });
  }, [form]);

  return (
    <form
      className="tw:flex tw:h-full tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-3 tw:overflow-hidden"
      onSubmit={(event) => {
        event.preventDefault();
        requestSave();
      }}
    >
      <div className="tw:shrink-0 tw:space-y-3">
        <div className="tw:flex tw:items-center tw:gap-2" data-tour="rule-name">
          <Switch
            checked={enabled}
            onCheckedChange={(next) => form.setValue('enabled', next === true)}
            aria-label="Enabled"
          />
          <Field className="tw:min-w-0 tw:flex-1">
            <FieldLabel htmlFor="rule-name">
              <span>
                Name
                <span aria-hidden="true"> *</span>
              </span>
            </FieldLabel>
            <Input
              id="rule-name"
              aria-required="true"
              {...form.register('name')}
            />
          </Field>
        </div>

        <Alert data-tour="reads-as">
          <AlertDescription className="tw:space-y-1">
            <p className="tw:m-0 tw:text-[11px] tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase">
              This rule reads as
            </p>
            <p className="tw:m-0 tw:text-sm tw:text-foreground">
              {aorText(readsAs)}
            </p>
          </AlertDescription>
        </Alert>
      </div>

      <div className="tw:flex tw:min-h-0 tw:flex-1 tw:gap-3 tw:overflow-hidden">
        <nav
          aria-label="Rule steps"
          className="tw:flex tw:w-80 tw:shrink-0 tw:flex-col tw:gap-1 tw:overflow-y-auto"
        >
          {RULE_STEP_NAV.map((step) => (
            <RuleStepNav
              key={step.id}
              index={step.index}
              title={step.title}
              summary={steps[step.id].summary}
              status={steps[step.id].status}
              selected={openStep === step.id}
              onSelect={() => selectStep(step.id)}
            />
          ))}
        </nav>

        <div className="tw:min-h-0 tw:min-w-0 tw:flex-1 tw:overflow-y-auto tw:pr-0.5">
          {openStep === 'conditions' ? (
            <div data-tour="when" className="tw:space-y-3">
              <ConditionBuilder
                slice={currentLogicSlice}
                onChange={applyLogicSlice}
                aorOptions={aorOptions}
              />
              <StepMessages
                errors={steps.conditions.errors}
                warnings={steps.conditions.warnings}
              />
              <p className="tw:m-0 tw:font-mono tw:text-[11px] tw:text-muted-foreground">
                {aorText(formulaPreview)}
              </p>
              <Field data-tour="check-frequency">
                <FieldLabel className="tw:flex tw:items-center tw:gap-1">
                  Check Frequency
                  <HelpIcon
                    label="How often PRATUS re-reads this rule"
                    text="How often PRATUS re-reads this rule's data. Whether a match creates a new event or updates the last one is set under Prevent Duplicate Events."
                  />
                </FieldLabel>
                <Controller
                  control={form.control}
                  name="repeatPattern"
                  render={({ field }) => (
                    <SimpleSelect
                      value={field.value}
                      onChange={field.onChange}
                      options={frequencyOptions}
                    />
                  )}
                />
                <FieldDescription>
                  PRATUS reads this feed{' '}
                  {checkFrequencyLabel(values.repeatPattern).toLowerCase()}.
                  {readsFeed ? ` ${FEED_FREQUENCY_NOTE}` : null}
                </FieldDescription>
              </Field>
            </div>
          ) : null}

          {openStep === 'event' ? (
            <div data-tour="event" className="tw:space-y-3">
              <Controller
                control={form.control}
                name="eventType"
                render={({ field: presetField }) => (
                  <Controller
                    control={form.control}
                    name="customCategory"
                    render={({ field: customField }) => (
                      <CategoryFields
                        preset={presetField.value}
                        custom={customField.value ?? ''}
                        onPresetChange={presetField.onChange}
                        onCustomChange={customField.onChange}
                        customInputId="rule-custom-category"
                      />
                    )}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="severity"
                render={({ field }) => (
                  <SeverityToggle
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <div className="tw:space-y-2 tw:rounded-md tw:border tw:border-border tw:p-3">
                <div className="tw:flex tw:items-center tw:gap-2">
                  <Controller
                    control={form.control}
                    name="preventDuplicateEvents"
                    render={({ field }) => (
                      <Switch
                        checked={field.value !== false}
                        onCheckedChange={(next) =>
                          field.onChange(next === true)
                        }
                        aria-label="Prevent Duplicate Events"
                      />
                    )}
                  />
                  <FieldLabel className="tw:mb-0">
                    Prevent Duplicate Events
                  </FieldLabel>
                </div>
                {values.preventDuplicateEvents !== false ? (
                  <>
                    <Field>
                      <FieldLabel
                        htmlFor="duplicate-window-hours"
                        className="tw:sr-only"
                      >
                        Duplicate window hours
                      </FieldLabel>
                      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:text-sm">
                        <span>If this rule has fired within the past</span>
                        <Controller
                          control={form.control}
                          name="duplicateWindowHours"
                          render={({ field }) => (
                            <Input
                              id="duplicate-window-hours"
                              type="number"
                              step={1}
                              className="tw:w-24"
                              value={field.value ?? 24}
                              onChange={(event) => {
                                const next = event.target.value;
                                if (next.trim() === '') return;
                                const parsed = Number(next);
                                if (Number.isFinite(parsed))
                                  field.onChange(parsed);
                              }}
                              onBlur={field.onBlur}
                            />
                          )}
                        />
                        <span>hours, update the existing event.</span>
                      </div>
                    </Field>
                    <div className="tw:flex tw:items-center tw:gap-2">
                      <Controller
                        control={form.control}
                        name="notifyOnDataChange"
                        render={({ field }) => (
                          <Switch
                            checked={field.value !== false}
                            onCheckedChange={(next) =>
                              field.onChange(next === true)
                            }
                            aria-label="Notify recipients that its data changed"
                          />
                        )}
                      />
                      <FieldLabel className="tw:mb-0">
                        Notify recipients that its data changed.
                      </FieldLabel>
                    </div>
                  </>
                ) : (
                  <FieldDescription>
                    Each match creates a new event.
                  </FieldDescription>
                )}
              </div>
              <Field>
                <FieldLabel
                  htmlFor="event-name-template"
                  className="tw:flex tw:items-center tw:gap-1"
                >
                  Event name
                  <HelpIcon
                    label="How the event name is chosen"
                    text="A template for the created event's name. Tokens such as @ruleName and @featureName resolve from the live match when the rule fires. The example below only fills values already known in this form."
                  />
                </FieldLabel>
                <Controller
                  control={form.control}
                  name="eventName"
                  render={({ field }) => (
                    <Input
                      id="event-name-template"
                      ref={eventNameTemplateRef}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )}
                />
                <TemplateVariableChips
                  placeholders={eventNamePlaceholders}
                  onInsert={(token) =>
                    insertTemplatePlaceholder(
                      'eventName',
                      eventNameTemplateRef.current,
                      token
                    )
                  }
                />
                {eventNamePreview ? (
                  <FieldDescription>
                    Example:{' '}
                    <span className="tw:text-foreground">
                      {aorText(eventNamePreview)}
                    </span>
                  </FieldDescription>
                ) : null}
              </Field>
              <Field>
                <FieldLabel className="tw:flex tw:items-center tw:gap-1">
                  {aorText('AOR')}
                  <HelpIcon
                    label={aorText('How AOR differs from AOR intersect')}
                    text={aorText(
                      'Stamps an AOR on the created event; it does not trigger the rule. Use the AOR intersect condition in step 1 to trigger on AOR overlap.'
                    )}
                  />
                </FieldLabel>
                <Controller
                  control={form.control}
                  name="aorStrategy"
                  render={({ field }) => (
                    <SimpleSelect
                      value={field.value}
                      onChange={field.onChange}
                      options={[
                        { label: 'None', value: 'none' },
                        { label: 'Fixed AOR(s)', value: 'fixed' },
                        {
                          label: 'From Impacted Resources',
                          value: 'from_resources',
                        },
                        {
                          label: 'From Impacted AOR(s)',
                          value: 'from_impacted_aors',
                        },
                      ]}
                    />
                  )}
                />
                <FieldDescription>
                  Sets the region on the created event. It does not affect
                  whether the rule fires.
                </FieldDescription>
              </Field>
              {aorStrategy === 'fixed' ? (
                <Field>
                  <FieldLabel>{aorText('AORs')}</FieldLabel>
                  <Controller
                    control={form.control}
                    name="aorIds"
                    render={({ field }) => (
                      <MultiIdCombobox
                        options={aorOptions}
                        value={field.value ?? []}
                        onChange={field.onChange}
                        placeholder={aorText('Select AORs…')}
                      />
                    )}
                  />
                </Field>
              ) : null}
              {aorStrategy === 'from_resources' &&
              steps.event.warnings.length === 0 ? (
                <FieldDescription>
                  {aorText(
                    'Region is set from AOR names on the resources that fall inside the hazard when the rule fires.'
                  )}
                </FieldDescription>
              ) : null}
              {aorStrategy === 'from_impacted_aors' &&
              steps.event.warnings.length === 0 ? (
                <FieldDescription>
                  {aorText(
                    'Region is set from the AORs your AOR intersect condition(s) matched when the rule fires.'
                  )}
                </FieldDescription>
              ) : null}
              <Field>
                <FieldLabel
                  htmlFor="threshold-desc"
                  className="tw:flex tw:items-center tw:gap-1"
                >
                  Event description (why this fired)
                  <HelpIcon
                    label="What the event description is used for"
                    text="A sentence explaining why the rule fired. Variables resolve with the live match, and the result is saved on the event, shown as its Threshold detail, and available to notification messages as @thresholdDescription. It never affects whether the rule fires."
                  />
                </FieldLabel>
                <Controller
                  control={form.control}
                  name="thresholdDescription"
                  render={({ field }) => (
                    <Textarea
                      id="threshold-desc"
                      rows={2}
                      ref={thresholdTemplateRef}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )}
                />
                <TemplateVariableChips
                  placeholders={descriptionPlaceholders}
                  onInsert={(token) =>
                    insertTemplatePlaceholder(
                      'thresholdDescription',
                      thresholdTemplateRef.current,
                      token
                    )
                  }
                />
                {thresholdTemplatePreview ? (
                  <FieldDescription>
                    Example:{' '}
                    <span className="tw:text-foreground">
                      {thresholdTemplatePreview}
                    </span>
                  </FieldDescription>
                ) : null}
              </Field>
              <StepMessages
                errors={steps.event.errors}
                warnings={steps.event.warnings}
              />
            </div>
          ) : null}

          {openStep === 'notify' ? (
            <div data-tour="notify" className="tw:space-y-3">
              <Field>
                <FieldLabel>Channels</FieldLabel>
                <div className="tw:space-y-2">
                  {AUTOMATION_CHANNEL_OPTIONS.map((channel) => (
                    <CheckboxRow
                      key={channel.value}
                      checked={
                        values.notificationTypes?.includes(channel.value) ??
                        false
                      }
                      locked={'disabled' in channel && channel.disabled}
                      tooltip={
                        'tooltip' in channel ? channel.tooltip : undefined
                      }
                      onChange={(next) => {
                        if ('disabled' in channel && channel.disabled) return;
                        const current =
                          form.getValues('notificationTypes') ?? [];
                        form.setValue(
                          'notificationTypes',
                          next
                            ? [...current, channel.value]
                            : current.filter((v) => v !== channel.value)
                        );
                      }}
                      label={channel.label}
                    />
                  ))}
                </div>
              </Field>
              {values.notifyOnDataChange !== false ? (
                <>
                  <Field>
                    <FieldLabel>Channels for updates</FieldLabel>
                    <FieldDescription>
                      Used when an open event&apos;s data changes. Text and
                      voice are off by default so updates do not interrupt
                      people.
                    </FieldDescription>
                    <div className="tw:space-y-2">
                      {AUTOMATION_CHANNEL_OPTIONS.map((channel) => (
                        <CheckboxRow
                          key={channel.value}
                          checked={updateChannels.includes(channel.value)}
                          locked={'disabled' in channel && channel.disabled}
                          tooltip={
                            'tooltip' in channel ? channel.tooltip : undefined
                          }
                          onChange={(next) => {
                            if ('disabled' in channel && channel.disabled)
                              return;
                            form.setValue(
                              'updateNotificationTypes',
                              next
                                ? [...updateChannels, channel.value]
                                : updateChannels.filter(
                                    (v) => v !== channel.value
                                  )
                            );
                          }}
                          label={channel.label}
                        />
                      ))}
                    </div>
                  </Field>
                  <Field>
                    <FieldLabel>Wait at least</FieldLabel>
                    <SimpleSelect
                      value={String(
                        values.updateMinIntervalMinutes ??
                          DEFAULT_UPDATE_MIN_INTERVAL_MINUTES
                      )}
                      onChange={(next) =>
                        form.setValue('updateMinIntervalMinutes', Number(next))
                      }
                      options={UPDATE_INTERVAL_OPTIONS.map((option) => ({
                        label: option.label,
                        value: String(option.value),
                      }))}
                    />
                    <FieldDescription>
                      between update notifications for the same event. Changes
                      in between are included in the next one. Updates that
                      change nothing are not sent.
                    </FieldDescription>
                  </Field>
                </>
              ) : null}
              {smsPreview ? (
                <Field>
                  <FieldLabel>Text message preview</FieldLabel>
                  <pre className="tw:m-0 tw:rounded-md tw:border tw:border-border tw:bg-muted tw:p-2 tw:font-sans tw:text-xs tw:whitespace-pre-wrap">
                    {smsPreview}
                  </pre>
                  <FieldDescription>
                    {smsPreview.length} of {LINKED_SMS_MAX_LENGTH} characters.
                    The link opens this event in PRATUS after sign-in.
                  </FieldDescription>
                </Field>
              ) : null}
              <Field>
                <FieldLabel>Users</FieldLabel>
                <Controller
                  control={form.control}
                  name="userIds"
                  render={({ field }) => (
                    <MultiIdCombobox
                      options={userOptions}
                      value={field.value ?? []}
                      onChange={field.onChange}
                      placeholder="Select users…"
                    />
                  )}
                />
              </Field>
              <Field>
                <FieldLabel>Roles & Scopes</FieldLabel>
                <Controller
                  control={form.control}
                  name="roleScopeKeys"
                  render={({ field }) => (
                    <MultiIdCombobox
                      options={roleScopeOptions(roleScopeMembers)}
                      value={field.value ?? []}
                      onChange={field.onChange}
                      placeholder="Select roles & scopes…"
                    />
                  )}
                />
              </Field>
              <ReachabilityWarning
                userIds={values.userIds ?? []}
                teamIds={values.teamIds ?? []}
                roleScopeKeys={values.roleScopeKeys ?? []}
                notificationTypes={[
                  ...new Set([
                    ...(values.notificationTypes ?? []),
                    ...(values.notifyOnDataChange !== false
                      ? updateChannels
                      : []),
                  ]),
                ]}
              />
              <Field>
                <FieldLabel>Priority</FieldLabel>
                <Controller
                  control={form.control}
                  name="priority"
                  render={({ field }) => (
                    <SimpleSelect
                      value={field.value}
                      onChange={field.onChange}
                      options={[...AUTOMATION_PRIORITY_OPTIONS]}
                    />
                  )}
                />
              </Field>
              <div className="tw:space-y-1">
                <ToggleRow
                  checked={!!values.confirmReceipt}
                  onChange={(next) => form.setValue('confirmReceipt', next)}
                  label="Request that Users Confirm Receipt"
                />
                <FieldDescription>
                  In-app notifications gain a Confirm Receipt button. Other
                  channels are told to reply 1 to confirm — that reply is not
                  yet captured, so treat it as guidance for now.
                </FieldDescription>
              </div>
              <StepMessages
                errors={steps.notify.errors}
                warnings={steps.notify.warnings}
              />
            </div>
          ) : null}

          {openStep === 'checklist' ? (
            <div data-tour="checklist" className="tw:space-y-3">
              <FieldDescription>
                Items materialize when the rule fires. Click an item to edit its
                full template: status, point of contact and sub-tasks for Org
                Actions, channels and per-channel messages for Send
                Notification.
              </FieldDescription>

              <ChecklistConfigEditor
                items={checklistItems}
                onChange={setChecklistItems}
                userOptions={userOptions}
                teamOptions={teamOptions}
                addLabel="Add Item"
                reorderable
              />
            </div>
          ) : null}

          {openStep === 'assetChecklists' ? (
            <div data-tour="asset-checklists" className="tw:space-y-3">
              <CategoryChecklistAssets
                category={resolvedCategory(
                  String(form.watch('eventType') ?? ''),
                  String(form.watch('customCategory') ?? '')
                )}
                ruleName={String(form.watch('name') ?? '')}
                savedRuleName={savedRuleName}
                userOptions={userOptions}
                teamOptions={teamOptions}
              />
              <StepMessages
                errors={steps.assetChecklists.errors}
                warnings={steps.assetChecklists.warnings}
              />
            </div>
          ) : null}

          {rule?.last_fired_dedupe_key ? (
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              This rule has already opened an event for a matching occurrence.
              Later checks update that event.
            </p>
          ) : null}
        </div>
      </div>

      <div className="tw:shrink-0 tw:space-y-2 tw:border-t tw:border-border tw:bg-background tw:pt-3">
        {blockingErrors.map((message) => (
          <p key={message} className="tw:m-0 tw:text-xs tw:text-destructive">
            {aorText(message)}
          </p>
        ))}
        <div className="tw:flex tw:flex-wrap tw:gap-2" data-tour="rule-actions">
          {rule ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="tw:gap-1"
              disabled={previewRule.isPending}
              data-tour="rule-preview"
              onClick={async () => {
                setPreviewError(null);
                setPreviewResult(null);
                setPreviewOpen(true);
                try {
                  const result = await previewRule.mutateAsync({
                    id: rule.id,
                    draft: formToPayload(form.getValues(), aorNameById),
                    useDemoStorms: usesTropicalFeed,
                  });
                  setPreviewResult(result);
                } catch {
                  setPreviewError('Could not evaluate this rule right now.');
                }
              }}
            >
              {previewRule.isPending ? (
                <Loader2 className="tw:h-4 tw:w-4 tw:animate-spin" />
              ) : (
                <Eye className="tw:h-4 tw:w-4" />
              )}
              Preview
            </Button>
          ) : null}
          {rule ? (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="tw:gap-1"
              disabled={deleteRule.isPending}
              onClick={async () => {
                await deleteRule.mutateAsync(rule.id);
                onClose();
              }}
            >
              <Trash2 className="tw:h-4 tw:w-4" />
              Delete
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            disabled={saving || blockingErrors.length > 0}
            onClick={requestSave}
          >
            Save
          </Button>
          {hideCancel ? null : (
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
          )}
        </div>
      </div>

      <RulePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        isPending={previewRule.isPending}
        error={previewError}
        result={previewResult}
      />
      <SaveAutomationRuleDialog
        open={saveConfirmOpen}
        onOpenChange={setSaveConfirmOpen}
        intervalText={saveIntervalText}
        isPending={saving}
        onConfirm={(enabledOnSave) => {
          void persistSave(enabledOnSave);
        }}
      />
    </form>
  );
}

function StepMessages({
  errors,
  warnings,
}: {
  errors: string[];
  warnings: string[];
}) {
  const aorText = useAorText();
  if (errors.length === 0 && warnings.length === 0) return null;
  return (
    <div className="tw:space-y-1">
      {errors.map((message) => (
        <p key={message} className="tw:m-0 tw:text-xs tw:text-destructive">
          {aorText(message)}
        </p>
      ))}
      {warnings.map((message) => (
        <p key={message} className="tw:m-0 tw:text-xs tw:text-muted-foreground">
          {aorText(message)}
        </p>
      ))}
    </div>
  );
}
